import {
	useContext,
	useCallback,
	useMemo,
	useState,
	useEffect
} from "react";
import { BuilderContext } from "./BuilderContext";
import { Guid, newGuid } from "client_legacy/ndaw/guid";

export type BuilderEditorHandlers = {
	isActive: boolean;
	toolbarContainer: HTMLElement | null;
	activate(): void;
	deactivate(): void;
};

export type BuilderEditorOptions = {
	getHasChanges?: () => boolean;
};

export const useBuilderEditor = (options: BuilderEditorOptions = {}): BuilderEditorHandlers => {
	const {
		getHasChanges
	} = options;

	const {
		activeEditorId,
		setActiveEditorId,
		toolbarContainer,
		setGetHasChangesHandle
	} = useContext(BuilderContext);

	const [editorId] = useState((): Guid => newGuid());

	const activateHandler = useCallback(
		(): void => {
			setActiveEditorId(editorId);
		},
		[editorId, setActiveEditorId]
	);

	const deactivateHandler = useCallback(
		(): void => {
			setActiveEditorId(null);
		},
		[setActiveEditorId]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			if (!getHasChanges) {
				return;
			}

			setGetHasChangesHandle(editorId, getHasChanges);

			return () => setGetHasChangesHandle(editorId, null);
		},
		[
			editorId,
			getHasChanges,
			setGetHasChangesHandle
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			return () => deactivateHandler();
		},
		[deactivateHandler]
	);

	return useMemo(
		(): BuilderEditorHandlers => ({
			isActive: activeEditorId === editorId,
			activate: activateHandler,
			deactivate: deactivateHandler,
			toolbarContainer
		}),
		[
			activeEditorId,
			editorId,
			activateHandler,
			deactivateHandler,
			toolbarContainer
		]
	);
};
